import { useState, useRef } from 'react'
import QRCode from 'qrcode'
import { Toast } from 'antd-mobile'

import styles from './ProductShare.module.less'
import closeIcon from './assets/close.svg'
import googleIcon from './assets/google.svg'
import bingIcon from './assets/bing.svg'
import wechatIcon from './assets/wechat.svg'
import zaloIcon from './assets/zalo.svg'
import defafultCorpLogo from '@/assets/img/default/corp_avatar.png'

import { t } from '@/utils/i18n'
import { utils, image, storageUtil, uploadCommonEt } from '@/utils'
import useCorpInfoStore from '@/stores/corp-info'
import { getConfig } from '@/config/config'

const { to, downloadFile, getUuid, getImageBase64, urlAddParams, throttle } =
  utils
const { htmlElement2Png } = image
const { getLocalStorage } = storageUtil

interface IProps {
  image: string
  productName: string
  onClose: () => void
}

function ProductShare(props: IProps) {
  const productShareRef = useRef(null)
  const modalRef = useRef(null)
  const [qrcodeUrl, setQrcodeUrl] = useState('')
  const [corpLogoBase64, setCorpLogoBase64] = useState('')
  const [productImgBase64, setProductImgBase64] = useState('')
  const [enableDownload, setEnableDownload] = useState(false)
  const { corpInfo } = useCorpInfoStore()
  const { corpName, corpLogo } = corpInfo
  const canShare = window.webview && window.webview.systemShare
  const config = getConfig()
  const { site, ossRoot } = config

  let shareUrl = ''

  // 关闭弹窗
  const handleClose = () => {
    props.onClose()
  }

  // 点击蒙层时
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === modalRef.current) {
      handleClose()
    }
  }

  // 下载图片
  const downloadPng = throttle(async () => {
    if (!productShareRef.current) {
      return
    }

    // 进入商品详情页后，点击【分享】弹出弹层后点击【下载图片】
    uploadCommonEt(16)

    const createToast = Toast.show({
      icon: 'loading',
      content: t('生成图片中…'),
      duration: 0,
    })

    const productSharePng = await htmlElement2Png(productShareRef.current)

    createToast.close()
    handleClose()

    // app下载
    if (window.webview && window.webview.downloadMedia) {
      const params = JSON.stringify([
        {
          path: productSharePng.replace(/^data:image\/\w+;base64,/, ''),
          name: `${getUuid()}.png`,
          isUrl: false,
          isVideo: false,
        },
      ])

      window.webview.downloadMedia(params)
      return
    }

    // 浏览器下载
    const downloadToast = Toast.show({
      icon: 'loading',
      content: t('正在下载'),
      duration: 0,
    })

    downloadFile(productSharePng, `${getUuid()}.png`)
    downloadToast.close()
  }, 1000)

  // 分享链接
  const shareHomeUrl = throttle(() => {
    if (!canShare) {
      return
    }

    handleClose()
    window.webview.systemShare(`${props.productName}，${shareUrl}`)
  }, 1000)

  /**
   * 生成二维码并设置二维码URL。
   *
   * @returns {Promise<void>} 一个 Promise，表示生成二维码的异步操作。
   */
  async function setQrcode(): Promise<void> {
    const [err, res] = await to(
      QRCode.toDataURL(shareUrl, {
        width: 200,
        margin: 0.5,
      }),
    )

    if (err) {
      console.error(err)
      return
    }

    setQrcodeUrl(res)
  }

  /**
   * 设置货品图片并将其转换为Base64格式。
   */
  async function setProductImage() {
    let imageUrl = props.image

    if (imageUrl.startsWith(ossRoot)) {
      imageUrl += `?x-oss-process=image/resize,m_fill,w_638`
    }

    const [err, res] = await to(getImageBase64(imageUrl))

    if (err) {
      console.error(err)
      return
    }

    setProductImgBase64(res)
  }

  /**
   * 设置公司Logo并将其转换为Base64格式。
   *
   */
  async function setLogoImage() {
    let imageUrl = defafultCorpLogo

    if (corpLogo) {
      imageUrl = `${ossRoot}${corpLogo}?x-oss-process=image/resize,m_fill,w_110`
    }

    const [err, res] = await to(getImageBase64(imageUrl))

    if (err) {
      console.error(err)
      return
    }

    setCorpLogoBase64(res)
  }

  // 初始化
  async function init() {
    const { origin, href } = location

    shareUrl = urlAddParams(href.replace(origin, site), {
      source: 1,
      language: getLocalStorage('language') || 'en-US',
    })

    await setQrcode()
    await setProductImage()
    await setLogoImage()

    setTimeout(() => {
      setEnableDownload(true)
    }, 0)
  }

  init()

  return (
    <div className={styles['mask']} ref={modalRef} onClick={handleOverlayClick}>
      <div className={styles['modal']}>
        <img
          className={styles['close']}
          title="close"
          src={closeIcon}
          onClick={handleClose}
        />

        <div className={styles['main']} ref={productShareRef}>
          {/* 网站信息 */}
          <div className={styles['site']}>
            <img
              className={styles['site-logo']}
              src={corpLogoBase64}
              title="logo"
            />

            <div className={styles['site-info']}>
              <div className={styles['site-title']}>{corpName}</div>
              <div className={styles['site-desc']}>{t('好物分享')}</div>
            </div>
          </div>

          {/* 货品主图 */}
          <div className={styles['product-img-container']}>
            <img
              className={styles['product-img']}
              src={productImgBase64 || props.image}
              title="product-img"
            />
          </div>

          {/* 二维码 */}
          <div className={styles['share']}>
            <div className={styles['share-left']}>
              <div className={styles['share-tips']}>{t('扫码查看')}</div>

              <div className={styles['share-app']}>
                <img
                  className={styles['share-icon']}
                  src={googleIcon}
                  title="google"
                />
                <img
                  className={styles['share-icon']}
                  src={bingIcon}
                  title="bing"
                />
                <img
                  className={styles['share-icon']}
                  src={wechatIcon}
                  title="wechat"
                />
                <img
                  className={styles['share-icon']}
                  src={zaloIcon}
                  title="zalo"
                />
              </div>
            </div>

            <img
              className={styles['share-img']}
              src={qrcodeUrl}
              title="qrcode"
            />
          </div>
        </div>

        {/* 动作按钮 */}
        <div className={styles['footer']}>
          <div
            className={`${styles['dl-btn']} ${
              enableDownload ? '' : styles['disabled']
            }`}
            onClick={downloadPng}
          >
            {t('下载图片')}
          </div>

          {canShare && (
            <div className={styles['share-btn']} onClick={shareHomeUrl}>
              {t('分享链接')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductShare
